import { motion } from "framer-motion";
import { ChevronLeft, Check, Flame } from "lucide-react";
import { VIBES, CUISINES, DISH_TYPES, DIET_MODES } from "@/constants";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const VibeScreen = ({
  selectedVibe,
  setSelectedVibe,
  selectedCuisine,
  setSelectedCuisine,
  selectedDishType,
  setSelectedDishType,
  dietModes,
  toggleDietMode,
  generateRecipes,
  goBackToIngredients,
}) => {
  return (
    <motion.div
      key="vibe"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen flex flex-col p-6"
      data-testid="vibe-screen"
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={goBackToIngredients}
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center border-2 border-[#264653]/10"
          data-testid="back-to-ingredients-btn"
        >
          <ChevronLeft className="w-5 h-5 text-[#264653]" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-[#264653]">How are you feeling?</h2>
          <p className="text-[#264653]/60 text-sm">Pick your vibe</p>
        </div>
      </div>

      {/* Vibe Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6 stagger-children">
        {VIBES.map((vibe) => (
          <div
            key={vibe.id}
            onClick={() => setSelectedVibe(vibe.id)}
            className={`vibe-card ${selectedVibe === vibe.id ? "selected" : ""}`}
            data-testid={`vibe-card-${vibe.id}`}
          >
            <img 
              src={vibe.image} 
              alt={vibe.label}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <span className="text-3xl mb-1">{vibe.emoji}</span>
              <span className="font-bold text-lg">{vibe.label}</span>
            </div>
            {selectedVibe === vibe.id && (
              <div 
                className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: vibe.color }}
              >
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Cuisine Selection */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-[#264653]/60 uppercase tracking-wide mb-3">Cuisine Style</h3>
        <div className="flex flex-wrap gap-2">
          {CUISINES.map((cuisine) => {
            const isActive = selectedCuisine === cuisine.id;
            return (
              <button
                key={cuisine.id || 'any'}
                onClick={() => setSelectedCuisine(cuisine.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
                  isActive 
                    ? "border-current bg-current/10" 
                    : "border-[#264653]/10 bg-white hover:border-[#264653]/30"
                }`}
                style={{ color: isActive ? cuisine.color : "#264653" }}
                data-testid={`cuisine-${cuisine.id || 'any'}`}
              >
                <span className="text-lg">{cuisine.emoji}</span>
                <span className={`font-medium text-sm ${isActive ? "" : "text-[#264653]"}`}>{cuisine.label}</span>
                {isActive && <Check className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dish Type Selection */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-[#264653]/60 uppercase tracking-wide mb-3">Dish Type</h3>
        <div className="flex flex-wrap gap-2">
          {DISH_TYPES.map((dish) => {
            const isActive = selectedDishType === dish.id;
            return (
              <button
                key={dish.id}
                onClick={() => setSelectedDishType(dish.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
                  isActive 
                    ? "border-current bg-current/10" 
                    : "border-[#264653]/10 bg-white hover:border-[#264653]/30"
                }`}
                style={{ color: isActive ? dish.color : "#264653" }}
                data-testid={`dish-type-${dish.id}`}
              >
                <span className="text-lg">{dish.emoji}</span>
                <span className={`font-medium text-sm ${isActive ? "" : "text-[#264653]"}`}>{dish.label}</span>
                {isActive && <Check className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Diet Modes */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-[#264653]/60 uppercase tracking-wide mb-3">Dietary Preferences</h3>
        <div className="grid grid-cols-2 gap-2">
          {DIET_MODES.map((diet) => {
            const Icon = diet.icon;
            const isActive = dietModes[diet.id];
            return (
              <button
                key={diet.id}
                onClick={() => toggleDietMode(diet.id)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                  isActive 
                    ? "border-current bg-current/10" 
                    : "border-[#264653]/10 bg-white hover:border-[#264653]/30"
                }`}
                style={{ color: isActive ? diet.color : "#264653" }}
                data-testid={`diet-${diet.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? "bg-current/20" : "bg-[#264653]/5"}`}>
                  <Icon className="w-4 h-4" style={{ color: isActive ? diet.color : "#264653" }} />
                </div>
                <div className="text-left">
                  <span className={`font-medium text-sm ${isActive ? "" : "text-[#264653]"}`}>{diet.label}</span>
                </div>
                {isActive && <Check className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cook Button */}
      <button
        onClick={generateRecipes}
        disabled={!selectedVibe}
        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-auto"
        data-testid="cook-btn"
      >
        <Flame className="w-5 h-5" />
        Let's Cook!
      </button>
    </motion.div>
  );
};
